import { appError, err, ok, } from '@orbit-mind/shared-types';
/**
 * Builds a command from its parts.
 *
 * The generic keeps the payload tied to the kind, so a typo in either is a compile error rather
 * than a command that dispatches to the wrong handler at runtime.
 */
export function command(kind, payload, meta) {
    return { kind, payload, meta };
}
/**
 * Advances the map's local revision.
 *
 * Every command that changes the document calls this. `localRevision` minus `serverRevision` is
 * the count of unsent edits, and it is what `deriveSaveState` reads to decide whether the UI may
 * say "synced" — so a command that mutated without bumping it would make the app claim work was
 * saved that had never been sent.
 */
export function touch(map, meta) {
    return {
        ...map,
        localRevision: (map.localRevision + 1),
        updatedAt: meta.timestamp,
        updatedBy: meta.authorId,
    };
}
/**
 * Replaces one node, leaving every other node object untouched by reference.
 *
 * Identity preservation is not an optimisation here — it is what lets a test assert that a
 * command touched only what it claimed to, and what lets React skip re-rendering every other
 * node on the canvas.
 */
export function withNode(doc, node, meta) {
    return {
        map: touch(doc.map, meta),
        nodes: { ...doc.nodes, [node.id]: node },
        edges: doc.edges,
    };
}
/** Removes a set of nodes and every edge that touched one of them. */
export function withoutNodes(doc, ids, meta) {
    const nodes = {};
    for (const [id, node] of Object.entries(doc.nodes)) {
        if (!ids.has(id))
            nodes[id] = node;
    }
    const edges = {};
    for (const [id, edge] of Object.entries(doc.edges)) {
        // An edge to a node that no longer exists would fail the integrity check and render as a
        // line to nowhere, so deletion has to take them with it.
        if (ids.has(edge.sourceNodeId) || ids.has(edge.targetNodeId))
            continue;
        edges[id] = edge;
    }
    return { map: touch(doc.map, meta), nodes, edges };
}
/**
 * Sets an optional field, or removes it when the value is "none".
 *
 * Absent and empty must not both be representable, or undo cannot restore the original: an
 * inverse can write a value back, but a field the edit *added* has to be removed, and there is
 * no value that means "was not here". Writing `pinned: false` or `attachments: []` where the
 * field had been absent leaves the document structurally different from where it started.
 */
export function setOrDrop(target, key, value) {
    if (value === undefined) {
        const { [key]: _removed, ...rest } = target;
        return rest;
    }
    return { ...target, [key]: value };
}
/**
 * Looks up an edge that validation has already established exists.
 *
 * Throws rather than asserting non-null, so a broken precondition reports which edge was missing
 * instead of surfacing as a `TypeError` two frames later.
 */
export function requireEdge(doc, id) {
    const edge = doc.edges[id];
    if (edge === undefined)
        throw new Error(`edge not found after validation: ${id}`);
    return edge;
}
/** The parent of a node validation has already established is not the root. */
export function requireParentId(node) {
    if (node.parentId === null)
        throw new Error(`expected a non-root node: ${node.id}`);
    return node.parentId;
}
export function requirePresent(doc, id, code = 'NODE_NOT_FOUND') {
    const node = doc.nodes[id];
    if (node === undefined)
        return err(appError(code, `node not found: ${id}`));
    return ok(node);
}
/**
 * Refuses to edit a locked node.
 *
 * Locking is a user's explicit "leave this alone". Silently editing it anyway would make the
 * control meaningless, and the user would find out by noticing the change later.
 */
export function requireEditable(node) {
    if (node.locked)
        return err(appError('FORBIDDEN', `node is locked: ${node.id}`));
    return ok(undefined);
}
//# sourceMappingURL=shared.js.map